import React from "react";
import { useHistory } from "react-router-dom";
import {
  Spinner,
  Box,
  SpaceBetween,
  ColumnLayout,
  FormField,
  Link,
} from "@amzn/awsui-components-react-v3";
import ButtonDropdown, {
  ButtonDropdownProps,
} from "@amzn/awsui-components-react-v3/polaris/button-dropdown";
import { History } from "history";
import { DeleteTeamProps } from "src/app/deleteTeam/DeleteTeamModal";
import HierarchyUpArrowSvg from "../../svg/HierarchyUpArrowSvg";
import { LoadingState } from "../../http";
import OrphanTeam from "../../common/utils/OrphanTeam";
import { DisplayBanner } from "../../banner/bannerSlice";
import styles from "./hierarchy.module.css";

const ID_SEPARATOR = "_";

export enum HierarchyItemType {
  PARENT = "PARENT",
  CHILD = "CHILD",
  CURRENT = "CURRENT",
}

export interface ActionsEnabled {
  createWorkflowEnabled: boolean;
  moveWorkflowEnabled: boolean;
  deleteWorkflowEnabled: boolean;
}

export interface HierarchyItem {
  name: string;
  link?: string;
  teamId: string;
  description: string;
  reportsTo?: string;
  actionsEnabled?: ActionsEnabled;
  type: HierarchyItemType;
}

interface HierarchyRowProperties {
  item: HierarchyItem;
  actionsLoading: LoadingState;
  setDeleteTeamProps: (props: DeleteTeamProps) => void;
  setBanner: (banner: DisplayBanner | undefined) => void;
}

interface HierarchyOrphanRowProperties {
  teamId: string;
}

interface HierarchyProperties {
  parent?: HierarchyItem;
  current: HierarchyItem;
  childrenItems: HierarchyItem[];
  actionsLoading: LoadingState;
  setDeleteTeamProps: (props: DeleteTeamProps) => void;
  setBanner: (banner: DisplayBanner | undefined) => void;
}

function createActionItems(
  teamId: string,
  workflowsEnabled: ActionsEnabled
): ButtonDropdownProps.Item[] {
  const items: ButtonDropdownProps.Item[] = [];
  items.push({
    id: `${teamId}${ID_SEPARATOR}create`,
    text: "Create team",
    disabled: !workflowsEnabled.createWorkflowEnabled,
  });
  items.push({
    id: `${teamId}${ID_SEPARATOR}move`,
    text: "Move team",
    disabled: !workflowsEnabled.moveWorkflowEnabled,
  });
  items.push({
    id: `${teamId}${ID_SEPARATOR}delete`,
    text: "Delete team",
    disabled: !workflowsEnabled.deleteWorkflowEnabled,
  });
  return items;
}

function onActionSelected(
  event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
  history: History,
  showDeleteTeamModal: () => void
): void {
  const clickedActionId = event.detail.id;
  const splitId = clickedActionId.split(ID_SEPARATOR);
  const teamId = splitId[0];
  const action = splitId[1];

  switch (action) {
    case "create":
      launchCreateTeamWorkflow(teamId, history);
      break;
    case "move":
      launchMoveTeamWorkflow(teamId, history);
      break;
    case "delete":
      showDeleteTeamModal();
      break;
    default:
      break;
  }
}

function launchCreateTeamWorkflow(teamId: string, history: History): void {
  const createTeamLink = `/team/${teamId}/createTeam`;
  history.push(createTeamLink);
}

function launchMoveTeamWorkflow(teamId: string, history: History): void {
  const moveTeamLink = `/team/${teamId}/moveTeam`;
  history.push(moveTeamLink);
}

function getTeamActions(
  teamId: string,
  teamName: string,
  parentTeamId: string | undefined,
  history: History,
  actionsLoading: LoadingState,
  setDeleteTeamProps: (props: DeleteTeamProps) => void,
  setBanner: (banner: DisplayBanner | undefined) => void,
  actionsEnabled?: ActionsEnabled
): JSX.Element {
  const showDeleteTeamModal = () => {
    setBanner(undefined);
    setDeleteTeamProps({
      showModal: true,
      teamId,
      teamName,
      parentTeamId,
    });
  };

  if (!actionsEnabled) {
    return <div />;
  }
  switch (actionsLoading) {
    case LoadingState.NO_DATA:
    case LoadingState.LOADING:
      return (
        <Box margin={{ right: "xs" }}>
          <Spinner />
        </Box>
      );
    case LoadingState.REJECTED:
      return <div />;
    case LoadingState.RESOLVED:
      if (!actionsEnabled) {
        return <div />;
      }
      return (
        <ButtonDropdown
          items={createActionItems(teamId, actionsEnabled)}
          onItemClick={(event) =>
            onActionSelected(event, history, showDeleteTeamModal)
          }
          variant="icon"
          data-testid="action-button"
          expandToViewport={true}
        />
      );
    default:
      return <div />;
  }
}

const HierarchyRow: React.FC<HierarchyRowProperties> = ({
  item,
  actionsLoading,
  setDeleteTeamProps,
  setBanner,
}): JSX.Element => {
  const paddingLeft = item.type === HierarchyItemType.CHILD ? "xxl" : "l";
  const rowClass =
    item.type === HierarchyItemType.CURRENT
      ? styles.currentRow
      : styles.otherRow;
  const history = useHistory();
  return (
    <div className={`${styles.rowWrapper} ${rowClass} ${styles.rowContent}`}>
      <Box padding={{ left: paddingLeft }}>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {item.type === HierarchyItemType.PARENT && <HierarchyUpArrowSvg />}
          <FormField
            description={item.description}
            label={
              item.link ? <Link href={item.link}>{item.name}</Link> : item.name
            }
          />
        </SpaceBetween>
      </Box>
      {getTeamActions(
        item.teamId,
        item.name,
        item.reportsTo,
        history,
        actionsLoading,
        setDeleteTeamProps,
        setBanner,
        item.actionsEnabled
      )}
    </div>
  );
};

const HierarchyOrphanRow: React.FC<HierarchyOrphanRowProperties> = ({
  teamId,
}) => (
  <tr key={teamId}>
    <td>
      <Box padding="m">
        <OrphanTeam />
      </Box>
    </td>
  </tr>
);

const Hierarchy: React.FC<HierarchyProperties> = ({
  parent,
  current,
  childrenItems,
  actionsLoading,
  setDeleteTeamProps,
  setBanner,
}): JSX.Element => {
  return (
    <Box padding={{ top: "s", bottom: "s" }}>
      <ColumnLayout columns={1} borders="horizontal">
        {parent && (
          <HierarchyRow
            item={parent}
            actionsLoading={actionsLoading}
            setDeleteTeamProps={setDeleteTeamProps}
            setBanner={setBanner}
          />
        )}
        {parent === undefined && current.name !== "Amazon" ? (
          <HierarchyOrphanRow teamId={current.teamId} />
        ) : (
          <HierarchyRow
            item={current}
            actionsLoading={actionsLoading}
            setDeleteTeamProps={setDeleteTeamProps}
            setBanner={setBanner}
          />
        )}
        {childrenItems.map((child) => (
          <HierarchyRow
            item={child}
            actionsLoading={actionsLoading}
            setDeleteTeamProps={setDeleteTeamProps}
            setBanner={setBanner}
          />
        ))}
      </ColumnLayout>
    </Box>
  );
};

export default Hierarchy;
